import { useFormulaEvaluator } from "../../hooks";
import { hasColorOptions, setDependencies, findManufacturerProductId } from "./utils/productHelpers";

interface UseProductSelectionProps {
    measurements: any;
    materialSchema: any[];
    dependencies: any[];
    selectedValues: any;
    setSelectedValues: (updater: (prev: any) => any) => void;
    setChangeInitiated: (value: boolean) => void;
    gutterLengths?: { gutterLength: string; downspoutLength: string };
    overrideWithCalculatedValues?: boolean;
}

/**
 * Hook to handle product selection logic including dependencies and quantity calculations
 */
export function useProductSelection({
    measurements,
    materialSchema,
    dependencies,
    selectedValues,
    setSelectedValues,
    setChangeInitiated,
    gutterLengths,
    overrideWithCalculatedValues = true,
}: UseProductSelectionProps) {
    const { evaluateFormula } = useFormulaEvaluator();

    /**
     * Handles product value change (selection from dropdown)
     */
    const handleValueChange = (selectedValue: any, productCategory: string) => {
        const tempStateData = { ...selectedValues };

        // Check if the selected product has color options
        const hasColors = hasColorOptions(materialSchema, productCategory, selectedValue);

        // Only set changeInitiated if product doesn't have colors (will be set when color is selected)
        if (!hasColors) {
            setChangeInitiated(true);
        }

        // Preserve existing quantity if available, otherwise default to 1
        const existingQuantity = selectedValues[productCategory]?.quantity;
        const initialQuantity = existingQuantity !== undefined && existingQuantity >= 0 ? existingQuantity : 1;

        // Find manufacturerProductId from materialSchema for products without colors
        // Products with colors will get manufacturerProductId when color is selected
        const manufacturerProductId = !hasColors 
            ? findManufacturerProductId(materialSchema, productCategory, selectedValue)
            : undefined;

        // Set the selected product, reset color when main selection changes
        // Preserve manufacturerProductId if product doesn't have colors (it's set immediately)
        tempStateData[productCategory] = {
            quantity: initialQuantity,
            value: selectedValue,
            color: undefined,
            manufacturerProductId: manufacturerProductId,
        };

        // Set dependencies recursively, passing materialSchema to look up manufacturerProductId
        setDependencies(dependencies, tempStateData, productCategory, selectedValue, materialSchema);

        // Helper to safely parse string length to number
        const parseLength = (val: string | undefined): number => {
            if (!val) return 0;
            const parsed = parseFloat(val);
            return isNaN(parsed) ? 0 : parsed;
        };

        const gutterLength = parseLength(gutterLengths?.gutterLength);
        const downspoutLength = parseLength(gutterLengths?.downspoutLength);

        // Inject gutter measurements into the context
        const augmentedMeasurements = [
            ...(Array.isArray(measurements) ? measurements : []),
            { MeasurementType: "GutterLength", ActualValue: gutterLength },
            { MeasurementType: "DownspoutLength", ActualValue: downspoutLength },
        ];

        // Calculate quantities from formulas
        const calculatedResults = evaluateFormula(augmentedMeasurements, tempStateData);

        // Update quantity values (preserve user input if exists, otherwise use calculated)
        Object.keys(tempStateData).forEach((category) => {
            if (calculatedResults[category] !== null) {
                const selectedQty = selectedValues[category]?.quantity ?? 0;
                const calculatedQty = calculatedResults[category] || 0;

                tempStateData[category].quantity = overrideWithCalculatedValues
                    ? calculatedQty
                    : selectedQty > 0
                    ? selectedQty
                    : calculatedQty;
            }
        });

        setSelectedValues(tempStateData);
    };

    /**
     * Handles color change for products with color options
     */
    const handleColorChange = (productCategory: string, color: string | null, manufacturerProductId?: string) => {
        setChangeInitiated(true);
        setSelectedValues((prev: any) => {
            const existing = prev[productCategory] || { quantity: 1, value: "" };
            return {
                ...prev,
                [productCategory]: {
                    ...existing,
                    color: color || undefined,
                    manufacturerProductId,
                },
            };
        });
    };

    /**
     * Handles quantity updates (add/remove)
     */
    const handleQuantityUpdate = (
        productCategory: any,
        eventType: "add" | "remove",
        additionalItem = false,
        cartItems?: any[]
    ) => {
        const delta = eventType === "add" ? 1 : -1;
        setChangeInitiated(true);

        if (additionalItem) {
            // Handle additional products (from mini inventory)
            setSelectedValues((prevValues: any) => {
                const identifier = `${productCategory.name}_${productCategory.product}`;
                const categoryData = prevValues[identifier];
                if (!categoryData) return prevValues;
                const newQuantity = Math.max(0, (categoryData.quantity || 0) + delta);
                // Allow 0 quantity - let cart sync handle deletion
                return {
                    ...prevValues,
                    [identifier]: {
                        ...categoryData,
                        quantity: newQuantity,
                    },
                };
            });
            return;
        }

        // Handle main product quantity updates
        setSelectedValues((prevValues: any) => {
            const categoryData = prevValues[productCategory];
            if (!categoryData) {
                // Initialize if doesn't exist (e.g., after deletion)
                if (eventType === "add") {
                    const cartItem = cartItems?.find((item: any) => item.ProductCategory === productCategory);
                    return {
                        ...prevValues,
                        [productCategory]: {
                            quantity: 1,
                            value: cartItem?.ProductName || "",
                            manufacturerProductId: cartItem?.ManufacturerProductId,
                        },
                    };
                }
                return prevValues;
            }
            const newQuantity = Math.max(0, (categoryData.quantity || 0) + delta);
            // Allow 0 quantity - let cart sync handle deletion
            return {
                ...prevValues,
                [productCategory]: {
                    ...categoryData,
                    quantity: newQuantity,
                },
            };
        });
    };

    return {
        handleValueChange,
        handleColorChange,
        handleQuantityUpdate,
    };
}
